import { useEffect, useState } from 'react';

import { useNavigate, useParams } from '@tanstack/react-router';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, Label, Textarea } from '@fade/ui';

import {
  useCreateLegacy,
  useLegacy,
  useUpdateLegacy,
} from '../lib/queries/legacies';

interface Props {
  mode: 'create' | 'edit';
}

function formatErr(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  return 'Something went wrong.';
}

export function LegacyEditPage({ mode }: Props) {
  const navigate = useNavigate();
  const params = useParams({ strict: false }) as { id?: string };

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const isEdit = mode === 'edit';
  const legacyId = params.id ?? '';

  const { data: existing, isPending } = useLegacy(isEdit ? legacyId : undefined);
  const createMut = useCreateLegacy();
  const updateMut = useUpdateLegacy(legacyId);

  useEffect(() => {
    if (existing) {
      setTitle(existing.title);
      setBody(existing.body);
      setExcerpt(existing.excerpt ?? '');
    }
  }, [existing]);

  async function onSave(nextStatus?: 'draft' | 'published') {
    setError(null);
    setSaving(true);
    try {
      if (isEdit) {
        // 1. Update content via mutation.
        await updateMut.mutateAsync({
          title,
          body,
          excerpt: excerpt.trim() ? excerpt.trim() : null,
        });
        // 2. Transition status via raw fetch (avoids React Query invalidation
        //    resetting route params before navigate).
        if (nextStatus) {
          const res = await fetch(`/api/me/legacies/${legacyId}/status`, {
            method: 'PUT',
            credentials: 'include',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ status: nextStatus }),
          });
          if (!res.ok) {
            const body = (await res.json()) as { error?: string };
            throw new Error(body?.error ?? `Status update failed (${res.status})`);
          }
        }
        await navigate({ to: '/legacies/$id', params: { id: legacyId } });
      } else {
        const created = await createMut.mutateAsync({
          title,
          body,
          ...(excerpt.trim() ? { excerpt: excerpt.trim() } : {}),
        });
        if (nextStatus) {
          const res = await fetch(`/api/me/legacies/${created.id}/status`, {
            method: 'PUT',
            credentials: 'include',
            headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ status: nextStatus }),
          });
          if (!res.ok) {
            const errBody = (await res.json()) as { error?: string };
            throw new Error(errBody?.error ?? `Status update failed (${res.status})`);
          }
          await navigate({ to: '/legacies/$id', params: { id: created.id } });
        } else {
          await navigate({ to: '/legacies/$id/edit', params: { id: created.id } });
        }
      }
    } catch (err) {
      setError(formatErr(err));
    } finally {
      setSaving(false);
    }
  }

  if (isEdit && isPending) {
    return (
      <main className="mx-auto max-w-2xl px-6 py-16">
        <p className="text-muted-foreground">Loading…</p>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-2xl px-6 py-16">
      <Card>
        <CardHeader>
          <CardTitle>{isEdit ? 'Edit story' : 'Write a new story'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              required
              maxLength={200}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="excerpt">Excerpt (optional)</Label>
            <Input
              id="excerpt"
              maxLength={400}
              placeholder="A short standfirst shown on feed cards."
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="body">Story</Label>
            <Textarea
              id="body"
              maxLength={200000}
              placeholder="Write your story in plain text. Markdown rendering arrives in stage 3."
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="min-h-[280px] font-serif"
            />
            <p className="text-xs text-muted-foreground">{body.length} / 200,000 characters</p>
          </div>

          {error ? <p className="text-sm text-destructive">{error}</p> : null}

          <div className="flex flex-wrap gap-2">
            <Button
              onClick={() => onSave('draft')}
              variant="secondary"
              disabled={saving}
            >
              Save draft
            </Button>
            <Button
              onClick={() => onSave('published')}
              disabled={saving}
            >
              {isEdit ? 'Publish' : 'Save & publish'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </main>
  );
}